import { useState, useEffect } from 'react'
import { Row, Col, Card, Form, Button, Badge, Alert, Spinner } from 'react-bootstrap'
import api from '../services/api'
import { useToast } from '../contexts/ToastContext'

function Invite() {
  const [inviteType, setInviteType] = useState('email')
  const [emailAddress, setEmailAddress] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [invites, setInvites] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const { showSuccess, showError } = useToast()

  useEffect(() => {
    fetchInvites()
  }, [])

  const fetchInvites = async () => {
    try {
      const response = await api.get('/invites')
      setInvites(response.data.invites)
      setStats({
        total_sent: response.data.total_sent,
        remaining: response.data.remaining,
        max_invites: response.data.max_invites
      })
    } catch (error) {
      showError('Failed to load invites')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (inviteType === 'email' && !emailAddress) {
      showError('Please enter an email address')
      return
    }
    if (inviteType === 'phone' && !phoneNumber) {
      showError('Please enter a phone number')
      return
    }

    setSending(true)
    try {
      const payload = {
        invite_type: inviteType,
        email_address: inviteType === 'email' ? emailAddress : null,
        phone_number: inviteType === 'phone' ? phoneNumber : null
      }

      const response = await api.post('/invites', payload)
      showSuccess('Invite sent successfully!')

      // Reset form
      setEmailAddress('')
      setPhoneNumber('')

      // Refresh invites list
      fetchInvites()
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to send invite')
    } finally {
      setSending(false)
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return <Badge bg="success">Accepted</Badge>
      case 'pending':
        return <Badge bg="warning" text="dark">Pending</Badge>
      case 'expired':
        return <Badge bg="secondary">Expired</Badge>
      default:
        return <Badge bg="secondary">{status}</Badge>
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div>
      <h2>Invite Friends</h2>
      <p className="text-muted">Invite others to join Bagel and build your trust network.</p>

      <Row className="mt-4">
        <Col lg={6}>
          <Card>
            <Card.Body>
              <Card.Title>Send an Invite</Card.Title>

              {stats && stats.remaining === 0 ? (
                <Alert variant="warning">
                  You have used all {stats.max_invites} invites.
                </Alert>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Invite via</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        label="Email"
                        name="inviteType"
                        checked={inviteType === 'email'}
                        onChange={() => setInviteType('email')}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Phone"
                        name="inviteType"
                        checked={inviteType === 'phone'}
                        onChange={() => setInviteType('phone')}
                      />
                    </div>
                  </Form.Group>

                  {inviteType === 'email' ? (
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="friend@example.com"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                      />
                    </Form.Group>
                  ) : (
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="+14155551234"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Use international format (e.g., +14155551234)
                      </Form.Text>
                    </Form.Group>
                  )}

                  <Button type="submit" disabled={sending}>
                    {sending ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Sending...
                      </>
                    ) : (
                      'Send Invite'
                    )}
                  </Button>
                </Form>
              )}

              {stats && (
                <div className="mt-3 text-muted">
                  <small>
                    {stats.remaining} of {stats.max_invites} invites remaining
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card>
            <Card.Body>
              <Card.Title>Sent Invites</Card.Title>

              {invites.length === 0 ? (
                <p className="text-muted">You haven't sent any invites yet.</p>
              ) : (
                <div className="invite-list">
                  {invites.map((invite) => (
                    <div key={invite.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <div>
                        <div>
                          {invite.invite_type === 'email' ? invite.email_address : invite.phone_number}
                        </div>
                        <small className="text-muted">
                          {new Date(invite.created).toLocaleDateString()}
                        </small>
                      </div>
                      {getStatusBadge(invite.status)}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default Invite
